import { SettingsContext } from 'core/components/settingsContext';
import { useFiltersFromUrl } from 'core/hooks/useFiltersFromUrl';
import useQuery from 'core/hooks/useQuery';
import { column, container, row, title } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import React, { useCallback, useContext } from 'react';
import ProductFormList from 'site/components/product-form/ProductFormList';
import { ProductFormOrderEnum, ProductFormPublicTypeEnum } from 'site/graphql';
import CoursesFilters from '../courses-filters/CoursesFilters';
import SliderFilter from '../courses-filters/SliderFilter';
import TextbooksList from '../textbooks/TextbooksList';
import useCourseListData from './hooks/useCourseListData';

export type ISlideState = 'TEXTBOOKS' | 'COURSES' | 'VIDEO_COURSES';

const CourseList: React.FC<WithStyles<typeof styles>> = ({ classes }) => {
    const settings = useContext(SettingsContext);
    const { types, slideState } = useFiltersFromUrl();
    const [isAllItemsDownloaded, loadMore, fetch, productForms, loading, refetch] = useCourseListData();
    const { changeQuery } = useQuery();

    const handleResetFilters = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();

            fetch({
                types: undefined,
                order: undefined,
                direction: undefined,
                complexity: undefined,
                tags: undefined,
                isFree: undefined,
            });
        },
        [fetch],
    );

    const changeSlide = useCallback(
        (newSlideState: ISlideState) => {
            switch (newSlideState) {
                case 'TEXTBOOKS':
                    changeQuery({ slideState: 'TEXTBOOKS', types: undefined });
                    break;
                case 'VIDEO_COURSES':
                    fetch({
                        types: [ProductFormPublicTypeEnum.EventRecord, ProductFormPublicTypeEnum.VideoCourse],
                        order: ProductFormOrderEnum.Price,
                        direction: 'desc',
                        slideState: 'VIDEO_COURSES',
                    });
                    break;
                case 'COURSES':
                default:
                    fetch({
                        types: [ProductFormPublicTypeEnum.Webinar, ProductFormPublicTypeEnum.Seminar],
                        slideState: 'COURSES',
                    });
                    break;
            }
        },
        [fetch, changeQuery],
    );

    const isFreeFilterVisible = settings.coursesList.filter.isFree === undefined;
    const complexityFilterVisible = settings.coursesList.filter.complexity === undefined;
    const tagsFilterVisible = settings.coursesList.filter.tags === undefined;
    const showFilters = types !== 'MATERIAL' && (isFreeFilterVisible || complexityFilterVisible || tagsFilterVisible);

    return (
        <>
            <SliderFilter onSlideChanged={changeSlide} />
            <div className={classes.container}>
                {showFilters && (
                    <div className={classes.searchPanel}>
                        <CoursesFilters
                            complexityFilterVisible={complexityFilterVisible}
                            tagsFilterVisible={tagsFilterVisible}
                            isFreeFilterVisible={isFreeFilterVisible}
                            fetch={fetch}
                        />
                    </div>
                )}

                <div className={classes.wrap}>
                    <div className={classes.coursePanel}>
                        {slideState === 'TEXTBOOKS' ? (
                            <TextbooksList />
                        ) : (
                            <ProductFormList
                                mode="FULL"
                                NotFoundComponent={() => (
                                    <p className={classes.empty}>
                                        Пока что здесь ничего нет. <br />
                                        <a href="/" onClick={handleResetFilters}>
                                            Перейти к выбору других курсов
                                        </a>
                                    </p>
                                )}
                                extraClasses={{
                                    item: `${classes.listItem}`,
                                }}
                                changeFilter={changeSlide}
                                isAllItemsDownloaded={isAllItemsDownloaded}
                                loadMore={loadMore}
                                productForms={productForms}
                                refetch={refetch}
                                isFetching={loading}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        container: {
            ...container(theme),
        },
        oopsMessage: {
            marginTop: 5,
            fontSize: 16,
            textAlign: 'center',
        },
        wrap: {
            ...row(),
        },
        filterPanel: {
            display: 'none',
        },
        filterPanel__filterOpen: {
            display: 'block',
            background: 'white',

            [theme.breakpoints.down('sm')]: {
                position: 'fixed',
                zIndex: 100,
                top: 0,
                left: 0,
                width: '50%',
                height: '100vh',
                background: 'white',
                boxShadow: '1px 0 5px 0 rgba(0, 0, 0, 0.2)',
                overflow: 'auto',
            },

            [theme.breakpoints.only('xs')]: {
                width: '100%',
            },

            [theme.breakpoints.up('md')]: {
                ...column({ cols: 4 }),
                marginTop: -55,
                marginBottom: 16,
            },
        },
        coursePanel: {
            ...column(),
        },
        coursePanel__filterOpen: {
            [theme.breakpoints.up('md')]: {
                ...column({ cols: 8 }),
            },
        },
        list: {
            marginTop: 24,
            ...container(theme),
            [theme.breakpoints.up('sm')]: {
                maxWidth: 1140,
            },
        },
        listItem: {
            ...column(),
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '1.5rem',
            [theme.breakpoints.up('sm')]: {
                ...column({ cols: 6 }),
            },
            [theme.breakpoints.up('md')]: {
                ...column({ cols: 4 }),
            },
        },
        listItem__filterOpen: {
            ...column(),
            [theme.breakpoints.up('sm')]: {
                ...column({ cols: 6 }),
            },
        },
        title: {
            ...title({ mode: 'old', size: 'h1' }),
            marginTop: 27,
            marginBottom: 18,
            fontSize: '2.2rem',
        },
        searchPanel: {
            height: 44,
            marginBottom: 1.4 + 'rem',
            display: 'flex',
            flexFlow: 'row wrap',

            [theme.breakpoints.down('xs')]: {
                height: 'auto',
            },
        },
        searchPanelFilterIcon: {
            width: 24,
            height: 24,
            fill: theme.colors.primary,
        },
        searchPanelFilterText: {
            display: 'none',
            [theme.breakpoints.up('sm')]: {
                paddingLeft: 10,
                display: 'inline',
            },
        },
        searchPanelFilterTextSet: {},
        searchPanelFirstBlock: {
            ...column({ shrink: 1, cols: 4 }),
            [theme.breakpoints.up('md')]: {
                ...column({ cols: 4 }),
            },
        },
        searchPanelSecondBlock: {
            ...column({ cols: 8 }),
            [theme.breakpoints.up('md')]: {
                ...column({ cols: 8 }),
            },
        },
        searchPanelFilterToggler: {
            border: '1px solid #cacaca',
            display: 'flex',
            alignItems: 'center',
            padding: '5px 10px',
            lineHeight: 1,
            cursor: 'pointer',
            fontSize: '80% !important',
            backgroundColor: 'white',
        },
        searchPanelFilterTogglerSet: {
            borderColor: '#FBE797',
            backgroundColor: '#FBE797',
        },
        empty: {
            ...column(),
        },
    });

export default injectSheet(styles)(CourseList);
